import { Badge, Button } from "@portal/ui";
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { addDays, jstToday, useCalendarWeek, usePrefetchCalendarWeek } from "../../api/queries";
import { CalendarPicker } from "../../features/calendar/CalendarPicker";
import { calendarColor } from "../../features/calendar/calendar-color";
import { DayTimeline } from "../../features/calendar/DayTimeline";
import { eventsOnDay } from "../../features/calendar/day-layout";
import { HomeSection } from "../HomeSection";
import { Hint } from "../Hint";
import { CardStatus } from "./CardStatus";

const TZ = "Asia/Tokyo";
const day = new Intl.DateTimeFormat("ja-JP", {
  month: "long",
  day: "numeric",
  weekday: "short",
  timeZone: TZ,
});

/** 見出しのリンク先。日付は指定しない(Google カレンダーを開いた状態から見る。ユーザー要望 2026-10-07)。 */
const GOOGLE_CALENDAR_URL = "https://calendar.google.com/calendar/r";
const daysFromToday = (date: string) =>
  Math.round(
    (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${jstToday()}T00:00:00Z`)) / 86_400_000,
  );
/** 今日から何日ずれているかの呼び名。 */
const relativeLabel = (n: number): string =>
  n === 0 ? "今日" : n === 1 ? "明日" : n === -1 ? "昨日" : n > 0 ? `${n}日後` : `${-n}日前`;

/**
 * 今日の予定(SPEC §6.5)。前の日・次の日・今日のボタンで、ほかの日も見られる(ユーザー要望 2026-10-07)。本人が Google カレンダーを許可したときだけ出す。Google カレンダーの 1 日表示のように、
 * 時間軸に並べる(DayTimeline)。
 * 予定はポータルに保存せず、表示のたびに取得する。連携が無効な環境ではカードごと出さない。
 * 取得は週単位(2026-10-14)。同じ週の中で日を移るときは取り直さず、取った週から 1 日分を取り出す。
 */
export function TodayEventsCard({ className }: { className?: string }) {
  // 見ている日(YYYY-MM-DD)。未指定は今日
  const [date, setDate] = useState<string | undefined>(undefined);
  const shownDate = date ?? jstToday();
  const q = useCalendarWeek(shownDate);
  // 別の週へ移った直後は、取れるまで前の週の表示が残る。押した日を先に出し、予定の部分は読み込み中だと分かるようにする
  const busy = q.isPlaceholderData;
  usePrefetchCalendarWeek(q.data?.status === "connected" && !busy ? shownDate : undefined);
  if (q.data?.status === "disabled") return null;
  const connected = q.data?.status === "connected" ? q.data : null;
  const events = connected && !busy ? eventsOnDay(shownDate, connected.events) : [];
  const failed = connected?.calendars.filter((c) => c.failed) ?? [];
  // すべて取れなかったときは、「予定はありません」と言わない(取りこぼしを予定が無いように見せない)
  const allFailed =
    connected !== null && failed.length > 0 && failed.length === connected.calendars.length;
  return (
    <HomeSection
      data-testid="today-events-card"
      className={className}
      // 見出しは「Google カレンダー」へのリンク(新しいタブ)
      title={
        <a
          href={GOOGLE_CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 hover:underline"
        >
          Google カレンダー
          <ExternalLink aria-hidden="true" className="size-3.5 text-muted-foreground" />
        </a>
      }
      action={
        connected ? (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={date === undefined}
              onClick={() => setDate(undefined)}
            >
              今日
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="前の日"
              onClick={() => setDate(addDays(shownDate, -1))}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="次の日"
              onClick={() => setDate(addDays(shownDate, 1))}
            >
              <ChevronRight />
            </Button>
            <CalendarPicker shown={connected.calendars} />
          </div>
        ) : undefined
      }
    >
      <CardStatus isPending={q.isPending} isError={q.isError} refetch={() => q.refetch()} />
      {q.data?.status === "disconnected" && (
        <div className="flex items-center gap-1.5">
          {/* 同意画面からこのタブへ戻す認可の流れなので、新しいタブでは開かない */}
          <Button asChild size="sm">
            <a href="/api/calendar/connect">
              <CalendarDays />
              Google カレンダーを連携する
            </a>
          </Button>
          <Hint label="Google カレンダーの連携の説明">
            連携すると、予定をここに表示します。読み取りだけで、予定は保存しません。
          </Hint>
        </div>
      )}
      {connected && (
        // 見ている日を、大きく分かりやすく出す(今日以外は、今日からの日数を目立つバッジで。2026-10-07)
        <p data-testid="shown-date" className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums">
            {day.format(new Date(`${shownDate}T00:00:00+09:00`))}
          </span>
          <Badge variant={date ? "default" : "outline"}>
            {relativeLabel(daysFromToday(shownDate))}
          </Badge>
          {busy && (
            <span role="status" className="flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              読み込み中…
            </span>
          )}
        </p>
      )}
      {connected && connected.calendars.length > 1 && (
        // 重ねて表示しているカレンダーの凡例(色と名前)
        <ul aria-label="表示中のカレンダー" className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {connected.calendars.map((c) => (
            <li key={c.id} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block size-2.5"
                style={{ backgroundColor: calendarColor(c) }}
              />
              <span className={c.failed ? "text-muted-foreground line-through" : ""}>{c.name}</span>
            </li>
          ))}
        </ul>
      )}
      {connected && failed.length > 0 && (
        // 取れなかったカレンダーの予定は欠けている。黙って出さず、はっきり知らせて取り直せるようにする
        <div
          role="status"
          className="mb-2 flex flex-wrap items-center gap-2 text-xs text-destructive"
        >
          <span>
            取得できなかったカレンダーがあります。その予定は表示されていません(共有が外れたか、一時的なエラーです):{" "}
            {failed.map((c) => c.name).join("、")}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={q.isFetching}
            onClick={() => void q.refetch()}
          >
            再読み込み
          </Button>
        </div>
      )}
      {connected && (events.length > 0 || busy) && (
        // 読み込み中は、前の日の予定の上に半透明の膜をかけ、動きの印を出す(押したのに何も起きないように見えないように)
        <div aria-busy={busy} className="relative flex min-h-24 flex-col lg:min-h-0 lg:flex-1">
          {events.length > 0 && (
            <DayTimeline
              key={shownDate}
              date={shownDate}
              events={events}
              calendars={connected.calendars}
            />
          )}
          {busy && (
            <div
              aria-hidden="true"
              className="absolute inset-0 z-30 grid place-items-center bg-background/70"
            >
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}
      {connected && events.length === 0 && !busy && !allFailed && (
        <p className="text-sm text-muted-foreground">
          {date ? "この日の予定はありません。" : "今日の予定はありません。"}
        </p>
      )}
    </HomeSection>
  );
}
